/**
 * WHERE THIS BUSINESS'S PLAYERS MAY PLAY: on site, at home, or both. Two big
 * switches, each ON or OFF at a glance, each undoable in one tap. At least one
 * stays on — trying to switch both off is refused in plain words. Demo
 * organizations are never restricted.
 */
import { useCallback, useEffect, useState } from 'react';
import { Home, Loader2, Store } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import api from '../../../lib/api';

export const GoalPlayPlacesBar = ({ organizationId }) => {
    const { t } = useTranslation();
    const [places, setPlaces] = useState(null);
    const [busy, setBusy] = useState('');
    const p = (k) => t(`goalGame.playPlaces.${k}`);

    const load = useCallback(async () => {
        if (!organizationId) return;
        try {
            const res = await api.get('/goal-game/admin/play-places',
                { params: { organization_id: organizationId } });
            setPlaces(res.data);
        } catch (_e) { setPlaces(null); }
    }, [organizationId]);

    useEffect(() => { load(); }, [load]);

    const flip = async (place) => {
        if (busy || !places) return;
        setBusy(place);
        try {
            const res = await api.put('/goal-game/admin/play-places', {
                organization_id: organizationId, place, on: !places[place],
            });
            setPlaces(res.data);
            if (res.data.refused) toast.error(p('keepOne'));
            else toast.success(res.data[place] ? p('turnedOn') : p('turnedOff'));
        } catch (_e) {
            toast.error(p('failed'));
        } finally { setBusy(''); }
    };

    if (!places) return null;

    const Switch = ({ place, icon: Icon, label }) => (
        <button type="button" onClick={() => flip(place)} disabled={!!busy}
                aria-pressed={!!places[place]}
                data-testid={`gg-play-place-${place}`}
                className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold transition-colors ${
                    places[place]
                        ? 'bg-emerald-600 text-white border-emerald-600'
                        : 'bg-white dark:bg-slate-900 text-muted-foreground border-slate-300 dark:border-slate-700'}`}>
            {busy === place ? <Loader2 size={14} className="animate-spin" /> : <Icon size={15} />}
            {label}
            <span className="rounded px-1.5 py-0.5 text-[11px] font-black bg-black/15"
                  data-testid={`gg-play-place-${place}-state`}>
                {places[place] ? p('on') : p('off')}
            </span>
        </button>
    );

    return (
        <div data-testid="gg-play-places-bar"
             className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 dark:border-slate-700 px-4 py-3">
            <span className="text-sm font-semibold">{p('title')}</span>
            <Switch place="on_site" icon={Store} label={p('onSite')} />
            <Switch place="at_home" icon={Home} label={p('atHome')} />
            <span className="text-xs text-muted-foreground">{p('hint')}</span>
        </div>
    );
};
